import React, { useState, useEffect, useRef } from 'react';
import {
    map,
    pipe,
    propEq,
    filter,
    prop,
    includes,
    addIndex,
    findIndex,
    prepend,
    isEmpty
} from 'ramda';
import MultiSelectDropdown from '../multiSelectDropdown/MultiSelectDropdown';
import ErrorMessage from '../errorMessage/ErrorMessage';
import Button from '../button/Button';
import Modal from '../modal/Modal';
import { useTranslation } from 'react-i18next';
import { RESPONSE, GLOBAL_COUNTRY_IRI } from '../../js/_constants';
import { apiPatch } from '../../js/api';
import Server from '../../js/config/_server';
import api from '../../api';
import { userStore, playlistStore } from '../../store';
import { isNotEmpty, propNotEq } from '../../js/fpUtils';

function EditFallback({
    fallbackCountries,
    playlist,
    onCancel,
    onSuccess,
}) {
    const { t } = useTranslation();
    const { details } = userStore();
    const { getPlaylist, setSelectedPlaylist } = playlistStore();
    
    const globalOption = {
        value: GLOBAL_COUNTRY_IRI,
        label: 'Global'
    };

    const createCountryOptions = affiliatedCountries => {
        const options = affiliatedCountries.map(country => ({
            value: country[RESPONSE.ID],
            label: country.name
        }));
        return details.brandResponsible
            ? prepend(globalOption, options)
            : options;
    };

    const getSelectedFallbacks = userCountries => {
        const options = userCountries
            .filter(uCountry =>
                fallbackCountries.includes(uCountry[RESPONSE.ID])
            )
            .map(uCountry => ({
                value: uCountry[RESPONSE.ID],
                label: uCountry.name
            }));

        if (playlist.globalFallback) {
            return prepend(globalOption, options);
        }
        return options;
    };

    const [countryOptions, setCountryOptions] = useState(
        createCountryOptions(details.affiliatedCountries)
    );
    const [selectedCountries, setSelectedCountries] = useState<{ value: string; label: string }[]>([]);
    const initialSelectedCountries = useRef(
        getSelectedFallbacks(details.affiliatedCountries)
    );
    const [fallbackOverrides, setFallbackOverrides] = useState<any[]>([]);
    const [isOpenReplaceModal, setOpenReplaceModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isFallbackEdited, setIsFallbackEdited] = useState(false);

    useEffect(() => {
        setCountryOptions(createCountryOptions(details.affiliatedCountries));
    }, [fallbackCountries]);

    const onSelect = selectedOptions => {
        setSelectedCountries(selectedOptions);
        setIsFallbackEdited(true);
    };

    const [isSaving, setIsSaving] = useState(false);
    const onSave = async () => {
        try {
            setOpenReplaceModal(false);
            const url = `${Server.baseUrl}${playlist[RESPONSE.ID]}`;

            const selectedCountryIris = selectedCountries
                .filter(country => country.label !== 'Global')
                .map(prop('value'));

            const apiOptions = details.brandResponsible 
            ? {
                      body: {
                          fallbackCountries: selectedCountryIris,
                          globalFallback: selectedCountries.some(
                              country => country.label === 'Global'
                          )
                      }
            } : {
                    body: {
                        fallbackCountries: selectedCountryIris,
                    },
            };
            // @ts-ignore
            const updatedPlaylist = await apiPatch(url, apiOptions);
            // @ts-ignore
            getPlaylist(updatedPlaylist.uuid);
            // @ts-ignore
            await setSelectedPlaylist(updatedPlaylist);
            setIsSaving(false);
            onSuccess();
        } catch (error) {
            console.error('Error while saving fallback countries: ', error);
            setErrorMessage(
                'There was an error while trying to save fallback countries.'
            );
        }
    };

    const mapIndexed = addIndex(map);

    const handleSave = async () => {
        setIsSaving(true);

        // Array to store any current overrides that might be replaced by the user's selection
        let fallbacksToOverride: any[] = [];
        const isGlobalSelected =
            findIndex(propEq('Global', 'label'))(selectedCountries) > -1;

        // if global Selected check whether it has fallback to replace or not
        if (isGlobalSelected) {
            const currentGlobalFallback = await api.getGlobalFallbackPlaylist();

            if (
                !isEmpty(currentGlobalFallback[RESPONSE.EXPAND_COLLECTION]) &&
                currentGlobalFallback[RESPONSE.EXPAND_COLLECTION][0].title !==
                    playlist.title
            ) {
                fallbacksToOverride = [
                    ...fallbacksToOverride,
                    {
                        countryName: 'Global',
                        playlistTitle:
                            currentGlobalFallback[RESPONSE.EXPAND_COLLECTION][0]
                                .title
                    }
                ];
            }
        }

        // selected countries except (global, previous fallback countries)
        const selectedCountriesID = pipe(
            filter(propNotEq('Global', 'label')),
            filter(
                country => !includes(prop('value', country), fallbackCountries)
            ),
            map(prop('value'))
        )(selectedCountries);

        //get full metadata for countries
        const selectedCountriesFullMetadata: any[] = [];
        details.affiliatedCountries.map(country => {
            if (selectedCountriesID.includes(country['@id'])) {
                selectedCountriesFullMetadata.push(country);
            }
        });

        // get previous fallbacks for selected countries except (global, previous fallback countries)
        const previousFallbacksForSelectedCountries = await Promise.all(
            pipe(
                map(prop('iso3')),
                map(api.getFallbackPlaylist)
            )(selectedCountriesFullMetadata)
        );

        // merge all infos in one object (each country metadata with its fallback)
        const mergedFallbackInfo = mapIndexed(
            (ele, i) => ({
                ...ele,
                fallbackPlaylist:
                    previousFallbacksForSelectedCountries[i][
                        RESPONSE.EXPAND_COLLECTION
                    ]
            }),
            selectedCountriesFullMetadata
        );

        // filter countries with no fallback/global
        const countriesWithFallback = mergedFallbackInfo.filter(
            ele => !isEmpty(ele.fallbackPlaylist)
        );

        // will check if the country selected has fallback playlist to replace checking countries:
        // germany (deu) has fallback playlist with countries {deu, fra, ita} => will be replaced and add to prompt
        // germany (deu) has fallback playlist with global countries {usa, fra, ita} => will be replaced but not add to prompt
        const filteredMergedFallbackInfo = countriesWithFallback.filter(ele => {
            // check if a fallbackCountry has same selected country
            return ele.fallbackPlaylist[0].fallbackCountries.some(
                country => country === ele[RESPONSE.ID]
            );
        });

        fallbacksToOverride = [
            ...fallbacksToOverride,
            ...map(fallback => ({
                countryName: fallback.name,
                playlistTitle: fallback.fallbackPlaylist[0].title
            }))(filteredMergedFallbackInfo)
        ];

        if (isNotEmpty(fallbacksToOverride)) {
            setFallbackOverrides(fallbacksToOverride);
            setOpenReplaceModal(true);
        } else {
            onSave();
        }
    };

    return (
        <>
            <MultiSelectDropdown
                className="edit-fallback__dropdown"
                name="playlist-fallback-for"
                label={t('playlist_fallback_for')}
                options={countryOptions}
                onChange={onSelect}
                defaultValue={initialSelectedCountries.current}
                isFilterable
            />
            <div className="edit-fallback-buttons-wrapper">
                <Button
                    secondaryButton
                    text={t('cancel')}
                    disabled={isSaving}
                    clickHandler={onCancel}
                    name="cancel-edit-fallback"
                />
                <Button
                    text={t('save')}
                    isLoading={isSaving}
                    disabled={!isFallbackEdited}
                    clickHandler={handleSave}
                    name="save-edit-fallback"
                />
            </div>
            {errorMessage && <ErrorMessage message={errorMessage} />}
            <Modal
                show={isOpenReplaceModal}
                onClose={onCancel}
                onCancel={onCancel}
                onAction={onSave}
                localeAction={t('replace')}
                localeCancel={t('cancel')}
            >
                <h4>{t('playlist_replace_fallback')}</h4>
                <ul className="replacing-countries-list">
                    {fallbackOverrides?.map((previousFallback: any, i) => (
                        <li key={i} className="replacing-country">
                            {previousFallback.countryName} -{' '}
                            {previousFallback.playlistTitle}
                        </li>
                    ))}
                </ul>
            </Modal>
        </>
    );
}

export default EditFallback;
